

















































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Timeline, TimelineEventPropertiesResult, TimelineOptions, TimelineTimeAxisScaleType} from 'vis-timeline/esnext';
import 'vis-timeline/dist/vis-timeline-graph2d.min.css';
import '@/compiled-icons/calendar-month';
import '@/compiled-icons/calendar-today';
import '@/compiled-icons/calendar-week';
import '@/compiled-icons/eye';
import '@/compiled-icons/timer';
import {TIMELINE_DEFAULT_OPTIONS} from '@/views/timeline/timeline.defaults';
import {TIMELINE_UTILS_INSTANCE, TimelineViewSettings} from '@/views/timeline/services/timeline.utils';
import {TimelineDataStoreServiceType} from '@/views/timeline/services/timeline-data-store-service.type';
import {ButtonType} from '@/views/timeline/button.type';
import {ScaleType} from '@/views/timeline/services/scale.type';
import {MomentFormatSpecification, MomentInput} from 'moment';

@Component
export default class TimelineTemplate extends Vue {
    @Prop() dataProvider!: TimelineDataStoreServiceType;

    @Prop({
        default: () => {
            return {};
        },
        required: false
    }) options!: TimelineOptions;

    buttons: ButtonType[] = [];

    fabGlobalSettings: boolean = false; // true if the floating action button speed dial is clicked
    fabViewSettings: boolean = false; // true if the floating action button speed dial  is clicked

    scaleOptions: Map<TimelineTimeAxisScaleType, ScaleType> = new Map();

    private timelineOffsetTop: number = 0;  // time axis top offset

    private timeline: Timeline | null = null; // the timeline instance
    private timelineEl: HTMLElement | null = null; // the timeline instance

    private viewSettings: TimelineViewSettings = {
        scale: 'day',
        timerEnabled: false,
        extendedView: false
    };
    private floatingTableHeader: HTMLElement | null = null;
    private floatingGroupBar: HTMLElement | null = null;

    @Watch('dataProvider') onDataProviderChanged() {
        this.initialize();
    }

    @Watch('viewSettings.extendedView') onViewSettingsChanged() {
        this.initialize();
    }

    mounted() {

        // restore saved settings in local storage
        this.viewSettings = TIMELINE_UTILS_INSTANCE.restoreSettings(this.dataProvider.getUniqueStoreKey());

        const optionalButtons: ButtonType[] = [];
        if (this.dataProvider.getHasSimplifiedView()) {
            optionalButtons.push({
                uniqueId: 'extendedViewToggleButton',
                icon: this.viewSettings.extendedView ? 'zoom_out' : 'zoom_in',
                text: '',
                color: 'green',
                inactiveColor: 'green lighten-4',
                active: this.viewSettings.extendedView,
                onClick: (self: ButtonType) => {
                    this.viewSettings.extendedView = !this.viewSettings.extendedView;
                    self.icon = this.viewSettings.extendedView ? 'zoom_out' : 'zoom_in';
                    self.title = this.viewSettings.extendedView ? 'Details verstecken' : 'Details anzeigen';
                    self.active = this.viewSettings.extendedView;
                    TIMELINE_UTILS_INSTANCE.saveSettings(this.dataProvider.getUniqueStoreKey(), this.viewSettings);
                },
                iconProps: {
                    color: '#fff',
                    inactiveColor: '#000'
                },
                title: this.viewSettings.extendedView ? 'Details verstecken' : 'Details anzeigen'
            });
        }

        optionalButtons.push({
            uniqueId: 'extendedViewToggleButton',
            svgIcon: 'timer',
            text: '',
            color: 'orange',
            inactiveColor: 'orange lighten-4',
            active: this.viewSettings.timerEnabled,
            onClick: (self: ButtonType) => {
                this.toggleTimer();
                self.active = this.viewSettings.timerEnabled;
            },
            iconProps: {
                width: '22',
                height: '18',
                color: '#fff',
                inactiveColor: '#000'
            },
            title: this.viewSettings.timerEnabled ? 'Timer ausblenden' : 'Timer einblenden'
        });

        this.buttons = optionalButtons;

        // initialize after DOM element with ref timeline is rendered
        this.$nextTick(() => {
            this.initialize();
        });
    }

    beforeMount() {
        // register scroll listener
        window.addEventListener('scroll', this.repositionFixedTableHeader);
    }

    beforeDestroy() {
        // unregister scroll listener
        window.removeEventListener('scroll', this.repositionFixedTableHeader);
        if (this.timeline) {
            this.timeline.destroy();
        }
    }

    toggleTimer() {
        this.viewSettings.timerEnabled = !this.viewSettings.timerEnabled;
        if (this.timeline) {
            this.timeline.setOptions({
                showCurrentTime: this.viewSettings.timerEnabled
            });
            TIMELINE_UTILS_INSTANCE.saveSettings(this.dataProvider.getUniqueStoreKey(), this.viewSettings);
        }
    }

    initialize() {
        const timelineContainer: any = this.$refs.timeline;
        if (timelineContainer && timelineContainer instanceof HTMLElement) {
            this.timelineEl = timelineContainer;

            // set the axis top offset. so that it appears floating and always on top
            const timeLineContainerBoundingRect = timelineContainer.getBoundingClientRect();
            this.timelineOffsetTop = timeLineContainerBoundingRect.top;

            this.scaleOptions = this.dataProvider.getHasSimplifiedView() && !this.viewSettings.extendedView ?
                this.dataProvider.getScaleSettingsSimplified() : this.dataProvider.getScaleSettings();

            // initial setup of the timeline
            const timelineOptions: TimelineOptions = Object.assign(TIMELINE_DEFAULT_OPTIONS, this.options, {
                showCurrentTime: this.viewSettings.timerEnabled,
                timeAxis: {
                    scale: this.viewSettings.scale,
                    step: 1
                },
                moment: (inp?: MomentInput, format?: MomentFormatSpecification, strict?: boolean) => {
                    // use our own moment instance to format dates
                    return this.$moment(inp, format, strict);
                },
                onInitialDrawComplete: () => {
                    if (this.timeline) {
                        this.updateTableSize(this.timeline, this.viewSettings.scale);
                        this.repositionFixedTableHeader();
                    }
                }
            });

            // Create a Timeline based on our settings
            const items = this.dataProvider.getHasSimplifiedView() && !this.viewSettings.extendedView ?
                this.dataProvider.getItemsSimplified() : this.dataProvider.getItems();
            if (!this.timeline) {
                this.timeline = new Timeline(
                    timelineContainer,
                    items,
                    this.dataProvider.getGroups(),
                    timelineOptions
                );

                this.timeline.on('changed', () => {
                    this.repositionFixedTableHeader();
                });

                this.timeline.on('click', (event: TimelineEventPropertiesResult) => {
                    this.$emit('onclick', event);
                });
            } else {
                this.timeline.setItems(items);
                this.timeline.setGroups(this.dataProvider.getGroups());
                this.updateTableSize(this.timeline, this.viewSettings.scale);
            }
        }
        this.dataProvider.registerObserver({
            notify: () => {
                this.onProviderDataChanged();
            }
        });
    }


    /**
     * changes the scale setting on the timeline
     * @param scale the new scale to be set
     */
    setScale(scale: TimelineTimeAxisScaleType) {
        if (this.timeline) {
            this.timeline.setOptions({
                timeAxis: {scale}
            });
            this.updateTableSize(this.timeline, scale);
            this.viewSettings.scale = scale;
            TIMELINE_UTILS_INSTANCE.saveSettings(this.dataProvider.getUniqueStoreKey(), this.viewSettings);
        }
    }


    /**
     * Returns a specific scale configuration given e.g. a string 'day'
     * @param scale e.g. 'day'
     */
    getScaleSetting(scale: TimelineTimeAxisScaleType): ScaleType {
        const currentScaleSettings = this.dataProvider.getHasSimplifiedView() && !this.viewSettings.extendedView ?
            this.dataProvider.getScaleSettingsSimplified() : this.dataProvider.getScaleSettings();
        let scaleSetting = currentScaleSettings.get(scale);
        if (!scaleSetting) {
            scaleSetting = {
                columnWidth: 100,
                columnCount: 10,
                name: 'Standard',
                buttonColor: 'black',
                iconName: ''
            };
        }
        return scaleSetting;
    }


    /**
     * Calculates the table width based on fixed multipliers per view scale
     * @param timeline the timeline instance
     * @param scale the target scale, e.g. 'week'
     */
    updateTableSize(timeline: Timeline, scale: TimelineTimeAxisScaleType) {
        const timeUnit = TIMELINE_UTILS_INSTANCE.transformScaleToUnitOfTime(scale);

        const scaleSetting: ScaleType = this.getScaleSetting(scale);

        const now = this.$moment().hours(0).minutes(0).seconds(0).milliseconds(0);
        const endDate = this.$moment(now).add(scaleSetting.columnCount, timeUnit).toDate();
        const startDate = this.$moment(now).startOf(timeUnit).toDate();

        // 30 dates * 100 px = 3000 width
        let width: number | string = scaleSetting.columnWidth * scaleSetting.columnCount;

        if (width < window.screenX) {
            width = '100%';
        }

        timeline.setOptions({width, end: endDate, start: startDate});
    }


    /**
     * Returns the time axis HTML Element
     */
    private getFloatingTableHeader(): HTMLElement | null {
        if (!this.floatingTableHeader) {
            let result: HTMLElement | null = null;
            const elementRaw = this.$el.querySelector('.vis-panel.vis-top');
            if (elementRaw && elementRaw instanceof HTMLElement) {
                result = elementRaw;
            }
            this.floatingTableHeader = result;
        }
        return this.floatingTableHeader;
    }

    /**
     * Returns the time axis HTML Element
     */
    private getFloatingGroupBar(): HTMLElement | null {
        if (!this.floatingGroupBar) {
            let result: HTMLElement | null = null;
            const elementRaw = this.$el.querySelector('.vis-panel.vis-left .vis-content');
            if (elementRaw && elementRaw instanceof HTMLElement) {
                result = elementRaw;
            }
            this.floatingGroupBar = result;
        }
        return this.floatingGroupBar;
    }

    /**
     * updates the position of the floating time axis bar
     */
    private repositionFixedTableHeader() {
        const timeAxis = this.getFloatingTableHeader();
        const groupBar = this.getFloatingGroupBar();
        if (timeAxis && groupBar) {
            if (this.timelineEl) {
                const groupBarWidth = groupBar.getBoundingClientRect().width || 0;
                this.timelineEl.style.marginLeft = groupBarWidth + 'px';

                const timeAxisTargetY = this.$vuetify.application.top;
                const timeAxisTargetX = this.$vuetify.application.left + groupBarWidth - window.scrollX + 1;
                timeAxis.style.transform = 'translate(' + timeAxisTargetX + 'px, ' + timeAxisTargetY + 'px)';
            }

            const groupBarTargetX = this.$vuetify.application.left;
            const groupBarTargetY = this.$vuetify.application.top + 34 - window.scrollY;
            groupBar.style.transform = 'translate(' + groupBarTargetX + 'px, ' + groupBarTargetY + 'px)';
        }
    }

    private onProviderDataChanged() {
        if (this.timeline) {
            const items = this.dataProvider.getHasSimplifiedView() && !this.viewSettings.extendedView ?
                this.dataProvider.getItemsSimplified() : this.dataProvider.getItems();
            this.timeline.setItems(items);
            this.timeline.setGroups(this.dataProvider.getGroups());
            this.timeline.redraw();
        }
    }
}
